import { gql as apolloGql } from '@apollo/client';
import { useSubscription } from '@apollo/client/react';
import { Badge, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { teamAdvisorBadgeForName } from '@src/components/sco/Advisors/model/access';
import { ScoAdvisorEngagement, ScoUser } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { useEffect, useState } from 'react';
import { LuLock } from 'react-icons/lu';
type EngagementNavListItemTitleProps = {
  engagement?: ScoAdvisorEngagement | null;
  name: string;
  userId?: string | null;
  /** Force a Startups.com team role badge (when not derived from name). */
  badge?: string | null;
  /** Show a lock icon (unpaid users viewing team advisors). */
  locked?: boolean;
  /** Avatar image when no engagement user is available (locked static roster). */
  avatarUrl?: string | null;
  /**
   * Override avatar user (e.g. founder on My Founders nav — engagement.advisorUser is the
   * current advisor, not the row subject).
   */
  avatarUser?: ScoUser | null;
  isAI?: boolean;
};
const ENGAGEMENT_NAV_UNREAD_FRAGMENT = apolloGql`
  fragment EngagementNavUnread on ScoThread {
    id
    threadId
    currentUserUnreadCommentsCount
    lastActiveAt
  }
`;
export const EngagementNavListItemTitle = ({
  engagement,
  name,
  userId,
  badge,
  locked = false,
  avatarUrl,
  avatarUser: avatarUserProp,
  isAI
}: EngagementNavListItemTitleProps) => {
  const threadId = engagement?.chatThread?.threadId;
  const initialUnread = engagement?.chatThread?.currentUserUnreadCommentsCount ?? 0;
  const [unreadCount, setUnreadCount] = useState(initialUnread);
  useEffect(() => {
    setUnreadCount(initialUnread);
  }, [engagement?.id, initialUnread, threadId]);
  useSubscription(gql`
      subscription EngagementNavUnread($threadId: String!) {
        scoThreadComment(threadId: $threadId) {
          id
          thread {
            ...EngagementNavUnread
          }
        }
      }
      ${ENGAGEMENT_NAV_UNREAD_FRAGMENT}
    `, {
    variables: {
      threadId
    },
    skip: !threadId,
    onData: ({
      data
    }) => {
      const count = data.data?.scoThreadComment?.thread?.currentUserUnreadCommentsCount;
      if (typeof count === 'number') {
        setUnreadCount(count);
      }
    }
  });
  const teamBadge = badge ?? teamAdvisorBadgeForName(name);
  const showAi = isAI ?? !!engagement?.isAI;
  const avatarUser = avatarUserProp || engagement?.advisorUser || (avatarUrl ? {
    pictureUrl: avatarUrl,
    fullName: name
  } as ScoUser : undefined);
  return <HStack align={'flex-start'} gap={2.5} minWidth={0} width={'full'}>
      <UserAvatar avatarProps={{
      size: 'sm'
    }} isAI={showAi} noLink={true} user={avatarUser} userId={userId || undefined} />
      <VStack align={'stretch'} flex={1} gap={0} minWidth={0} paddingTop={'1px'}>
        <HStack align={'center'} gap={1.5} minWidth={0} width={'full'}>
          <Text flex={1} fontWeight={'semibold'} lineClamp={1} lineHeight={'short'} minWidth={0} textAlign={'start'} textStyle={'sm'}>
            {name}
          </Text>
          {unreadCount > 0 && !locked && <Badge flexShrink={0} variant={'count'}>
              {unreadCount}
            </Badge>}
          {locked ? <Icon as={LuLock} boxSize={3} color={'fg.muted'} flexShrink={0} /> : null}
        </HStack>
        {teamBadge ? <Text color={'fg.muted'} lineClamp={1} minWidth={0} textAlign={'start'} textStyle={'xs'}>
            {teamBadge}
          </Text> : null}
      </VStack>
    </HStack>;
};